"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CarbonWallet = void 0;
const constant_1 = require("../constant");
const provider_1 = require("../provider");
const util_1 = require("../util");
const address_1 = require("../util/address");
const fetch_1 = require("../util/fetch");
const generic_1 = require("../util/generic");
const number_1 = require("../util/number");
const tx_1 = require("../util/tx");
const amino_1 = require("@cosmjs/amino");
const stargate_1 = require("@cosmjs/stargate");
const tendermint_rpc_1 = require("@cosmjs/tendermint-rpc");
const tx_2 = require("cosmjs-types/cosmos/tx/v1beta1/tx");
const CarbonSigner_1 = require("./CarbonSigner");
const CarbonSigningClient_1 = require("./CarbonSigningClient");
class CarbonWallet {
    constructor(opts) {
        var _a, _b, _c, _d;
        this.defaultFeeDenom = constant_1.DEFAULT_FEE_DENOM;
        this.initialized = false;
        this.sequenceInvalidated = false;
        this.isNewAccountError = (error) => {
            var _a;
            return (_a = error === null || error === void 0 ? void 0 : error.message) === null || _a === void 0 ? void 0 : _a.includes("Account does not exist on chain. Send some tokens there before trying to query sequence.");
        };
        this.isNonceMismatchError = (error) => {
            const regex = /^Broadcasting transaction failed with code 32 \(codespace: sdk\)\. Log: account sequence mismatch, expected (\d+), got (\d+): incorrect account sequence/;
            const match = error === null || error === void 0 ? void 0 : error.message.match(regex);
            if (match) {
                return {
                    expected: match[1],
                    provided: match[2],
                };
            }
            return false;
        };
        const network = (_a = opts.network) !== null && _a !== void 0 ? _a : constant_1.DEFAULT_NETWORK;
        this.network = network;
        this.networkConfig = constant_1.NetworkConfigs[network];
        this.configOverride = (_b = opts.config) !== null && _b !== void 0 ? _b : {};
        this.providerAgent = opts.providerAgent;
        this.defaultTimeoutBlocks = (_c = opts.defaultTimeoutBlocks) !== null && _c !== void 0 ? _c : 30; // default ~1 minute
        this.disableRetryOnSequenceError = (_d = opts.disableRetryOnSequenceError) !== null && _d !== void 0 ? _d : false;
        this.updateNetwork(network);
        this.onRequestSign = opts.onRequestSign;
        this.onSignComplete = opts.onSignComplete;
        this.txDispatchManager = new generic_1.QueueManager(this.dispatchTx.bind(this));
        this.txSignManager = new generic_1.QueueManager(this.signTx.bind(this));
        this.mnemonic = opts.mnemonic;
        if (this.mnemonic) {
            this.privateKey = util_1.AddressUtils.SWTHAddress.mnemonicToPrivateKey(this.mnemonic);
        }
        else if (opts.privateKey) {
            this.privateKey = util_1.AddressUtils.stringOrBufferToBuffer(opts.privateKey);
        }
        const addressOpts = Object.assign({ network }, (this.configOverride.Bech32Prefix && {
            bech32Prefix: this.configOverride.Bech32Prefix,
        }));
        if (opts.signer) {
            this.signer = opts.signer;
            this.publicKey = Buffer.from(opts.publicKeyBase64, "base64");
            this.bech32Address = util_1.AddressUtils.SWTHAddress.publicKeyToAddress(this.publicKey, addressOpts);
        }
        else if (this.privateKey) {
            this.publicKey = util_1.AddressUtils.SWTHAddress.privateToPublicKey(this.privateKey);
            this.bech32Address = util_1.AddressUtils.SWTHAddress.publicKeyToAddress(this.publicKey, addressOpts);
            let prefix = addressOpts.bech32Prefix;
            if (!addressOpts.bech32Prefix)
                prefix = address_1.SWTHAddress.getBech32Prefix(addressOpts === null || addressOpts === void 0 ? void 0 : addressOpts.network, addressOpts === null || addressOpts === void 0 ? void 0 : addressOpts.bech32Prefix);
            if (!prefix)
                throw new Error("cannot instantiate wallet signer, no prefix");
            this.signer = new CarbonSigner_1.CarbonPrivateKeySigner(this.privateKey, prefix);
        }
        else if (opts.bech32Address) {
            // read-only wallet, without private/public keys
            this.signer = new CarbonSigner_1.CarbonNonSigner();
            this.publicKey = Buffer.from([]);
            this.bech32Address = opts.bech32Address;
        }
        else {
            throw new Error("cannot instantiate wallet signer");
        }
        const addressBytes = util_1.AddressUtils.SWTHAddress.getAddressBytes(this.bech32Address, this.network);
        this.hexAddress = `0x${Buffer.from(addressBytes).toString("hex")}`;
    }
    static withPrivateKey(privateKey, opts = {}) {
        return new CarbonWallet(Object.assign(Object.assign({}, opts), { privateKey }));
    }
    static withMnemonic(mnemonic, opts = {}) {
        return new CarbonWallet(Object.assign(Object.assign({}, opts), { mnemonic }));
    }
    static withLedger(cosmosLedger, publicKeyBase64, opts = {}) {
        const signer = new CarbonSigner_1.CarbonLedgerSigner(cosmosLedger);
        const wallet = CarbonWallet.withSigner(signer, publicKeyBase64, opts);
        return wallet;
    }
    static withSigner(signer, publicKeyBase64, opts = {}) {
        return new CarbonWallet(Object.assign(Object.assign({}, opts), { signer,
            publicKeyBase64 }));
    }
    static withKeplr(keplr, chainInfo, keplrKey, opts = {}) {
        const signer = provider_1.KeplrAccount.createKeplrSigner(keplr, chainInfo, keplrKey);
        const publicKeyBase64 = Buffer.from(keplrKey.pubKey).toString("base64");
        const wallet = CarbonWallet.withSigner(signer, publicKeyBase64, opts);
        return wallet;
    }
    static withAddress(bech32Address, opts = {}) {
        return new CarbonWallet(Object.assign(Object.assign({}, opts), { bech32Address }));
    }
    initialize(queryClient) {
        return __awaiter(this, void 0, void 0, function* () {
            this.query = queryClient;
            yield Promise.all([
                this.reconnectTmClient(),
                this.reloadTxFees(),
                this.reloadAccountSequence(),
            ]);
            this.initialized = true;
            return this;
        });
    }
    updateNetwork(network) {
        this.network = network;
        this.networkConfig = util_1.GenericUtils.overrideConfig(constant_1.NetworkConfigs[network], this.configOverride);
        delete this.signingClient;
        return this;
    }
    getSignedTx(signerAddress, messages, sequence, opts) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { memo = "", explicitSignerData, feeDenom, } = opts;
            const signingClient = this.getSigningClient();
            const [account] = yield this.signer.getAccounts();
            let signature = null;
            try {
                yield util_1.GenericUtils.callIgnoreError(() => { var _a; return (_a = this.onRequestSign) === null || _a === void 0 ? void 0 : _a.call(this, messages); });
                const signerData = Object.assign({ accountNumber: this.accountInfo.accountNumber, chainId: this.getChainId(), sequence }, explicitSignerData);
                const fee = (_a = opts === null || opts === void 0 ? void 0 : opts.fee) !== null && _a !== void 0 ? _a : this.estimateTxFee(messages, feeDenom);
                const txRaw = yield signingClient.sign(signerAddress, messages, fee, memo, signerData);
                signature = (0, amino_1.encodeSecp256k1Signature)(account.pubkey, txRaw.signatures[0]);
                return txRaw;
            }
            finally {
                yield util_1.GenericUtils.callIgnoreError(() => { var _a; return (_a = this.onSignComplete) === null || _a === void 0 ? void 0 : _a.call(this, signature); });
            }
        });
    }
    /**
     * broadcast TX and wait for block confirmation
     *
     */
    broadcastTx(txRaw, opts = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { pollIntervalMs = 3000, timeoutMs = 60000 } = opts;
            const tx = CarbonWallet.TxRaw.encode(txRaw).finish();
            const carbonClient = this.getSigningClient();
            const response = yield carbonClient.broadcastTx(tx, timeoutMs, pollIntervalMs);
            if ((0, stargate_1.isDeliverTxFailure)(response)) {
                // tx failed
                console.error(response);
                throw new Error(`[${response.code}] ${response.rawLog}`);
            }
            return response;
        });
    }
    /**
     * broadcast TX to mempool but doesnt wait for block confirmation
     *
     */
    broadcastTxWithoutConfirm(txRaw) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = CarbonWallet.TxRaw.encode(txRaw).finish();
            const tmClient = this.getTmClient();
            return tmClient.broadcastTxSync({ tx });
        });
    }
    signAndBroadcast(messages, signOpts, broadcastOpts) {
        return __awaiter(this, void 0, void 0, function* () {
            const promise = new Promise((resolve, reject) => {
                this.txSignManager.enqueue({
                    signerAddress: this.bech32Address,
                    messages,
                    broadcastOpts,
                    signOpts,
                    handler: { resolve, reject },
                });
            });
            return promise;
        });
    }
    signTx(txRequest) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { reattempts, signerAddress, signOpts, messages, broadcastOpts, handler: { resolve, reject }, } = txRequest;
            try {
                if (!this.accountInfo || this.sequenceInvalidated)
                    yield this.reloadAccountSequence();
                const heightResponse = yield (0, fetch_1.fetch)(`${this.networkConfig.tmRpcUrl}/blockchain?cache=${new Date().getTime()}`);
                const heightRes = yield heightResponse.json();
                const height = (0, number_1.bnOrZero)((_a = heightRes.result) === null || _a === void 0 ? void 0 : _a.last_height);
                const timeoutHeight = height.isZero() ? undefined : height.toNumber() + this.defaultTimeoutBlocks;
                const sequence = this.accountInfo.sequence;
                this.accountInfo = Object.assign(Object.assign({}, this.accountInfo), { sequence: sequence + 1 });
                const _signOpts = Object.assign(Object.assign({}, signOpts), { explicitSignerData: Object.assign({ timeoutHeight }, signOpts === null || signOpts === void 0 ? void 0 : signOpts.explicitSignerData) });
                const signedTx = yield this.getSignedTx(signerAddress, messages, sequence, _signOpts);
                this.txDispatchManager.enqueue({
                    reattempts,
                    signerAddress,
                    messages,
                    signedTx,
                    broadcastOpts,
                    signOpts: _signOpts,
                    handler: { resolve, reject, requestId: `${sequence}` },
                });
            }
            catch (error) {
                reject(error);
            }
        });
    }
    dispatchTx(txRequest) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { broadcastOpts, signedTx, handler: { resolve, reject }, } = txRequest;
            const broadcastFunc = (broadcastOpts === null || broadcastOpts === void 0 ? void 0 : broadcastOpts.mode) === tx_1.BroadcastTxMode.BroadcastTxSync ? this.broadcastTxWithoutConfirm.bind(this) : this.broadcastTx.bind(this);
            try {
                const result = yield broadcastFunc(signedTx, broadcastOpts);
                resolve(result);
            }
            catch (error) {
                const reattempts = (_a = txRequest.reattempts) !== null && _a !== void 0 ? _a : 0;
                // retry sendTx if nonce error once.
                if (!this.disableRetryOnSequenceError && reattempts < 1 && this.isNonceMismatchError(error)) {
                    // invalidate account sequence for reload on next signTx call
                    this.sequenceInvalidated = true;
                    // requeue transaction for signTx
                    this.txSignManager.enqueue({
                        reattempts: (reattempts !== null && reattempts !== void 0 ? reattempts : 0) + 1,
                        signerAddress: txRequest.signerAddress,
                        messages: txRequest.messages,
                        broadcastOpts,
                        signOpts: txRequest.signOpts,
                        handler: { resolve, reject },
                    });
                }
                else {
                    reject(error);
                }
            }
        });
    }
    sendTxs(msgs, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.signAndBroadcast(msgs, opts, { mode: tx_1.BroadcastTxMode.BroadcastTxBlock });
            return result;
        });
    }
    sendTxsWithoutConfirm(msgs, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.signAndBroadcast(msgs, opts, { mode: tx_1.BroadcastTxMode.BroadcastTxSync });
            return result;
        });
    }
    sendTx(msg, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.sendTxs([msg], opts);
        });
    }
    getSigningClient() {
        const tmClient = this.getTmClient();
        if (!this.signingClient) {
            this.signingClient = new CarbonSigningClient_1.CarbonSigningClient(tmClient, this.signer);
        }
        return this.signingClient;
    }
    getTmClient() {
        if (!this.tmClient)
            throw new Error("CarbonWallet is not initialized");
        return this.tmClient;
    }
    getChainId() {
        if (!this.chainId)
            throw new Error("CarbonWallet is not initialized");
        return this.chainId;
    }
    isSigner(signerType) {
        return this.signer.type === signerType;
    }
    isLedgerSigner() {
        return this.isSigner(CarbonSigner_1.CarbonSignerTypes.Ledger);
    }
    isPrivateKeySigner() {
        return this.isSigner(CarbonSigner_1.CarbonSignerTypes.PrivateKey);
    }
    isBrowserInjectedSigner() {
        return this.isSigner(CarbonSigner_1.CarbonSignerTypes.BrowserInjected);
    }
    getGasCost(msgTypeUrl) {
        var _a, _b, _c, _d;
        if (!this.txGasCosts) {
            console.warn("tx gas costs not initialized");
        }
        return (_d = (_b = (_a = this.txGasCosts) === null || _a === void 0 ? void 0 : _a[msgTypeUrl]) !== null && _b !== void 0 ? _b : (_c = this.txGasCosts) === null || _c === void 0 ? void 0 : _c[util_1.CarbonTx.TxGasCostTypeDefaultKey]) !== null && _d !== void 0 ? _d : number_1.BN_ZERO;
    }
    getGasPrice(denom) {
        var _a;
        if (!this.txGasPrices) {
            console.warn("tx gas prices not initialized");
        }
        const gasPrice = (_a = this.txGasPrices) === null || _a === void 0 ? void 0 : _a[denom];
        if (!gasPrice) {
            console.warn("denom not supported for paying gas");
        }
        return gasPrice !== null && gasPrice !== void 0 ? gasPrice : number_1.BN_ZERO;
    }
    getFee(msgTypeUrl, denom = this.defaultFeeDenom) {
        const minGasPrice = this.getGasPrice(denom);
        const msgGasCost = this.getGasCost(msgTypeUrl);
        return msgGasCost.times(minGasPrice);
    }
    reconnectTmClient() {
        return __awaiter(this, void 0, void 0, function* () {
            this.tmClient = yield tendermint_rpc_1.Tendermint34Client.connect(this.networkConfig.tmRpcUrl);
            const status = yield this.tmClient.status();
            this.chainId = status.nodeInfo.network;
        });
    }
    reloadTxFees() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const queryClient = this.getQueryClient();
            const { msgGasCosts } = yield queryClient.fee.MsgGasCostAll({});
            this.txGasCosts = msgGasCosts.reduce((result, item) => {
                result[item.msgType] = (0, number_1.bnOrZero)(item.gasCost);
                return result;
            }, {});
            const { minGasPrices } = yield queryClient.fee.MinGasPriceAll({});
            this.txGasPrices = minGasPrices.reduce((result, item) => {
                result[item.denom] = (0, number_1.bnOrZero)(item.gasPrice).shiftedBy(-18); // sdk.Dec shifting
                return result;
            }, {});
            if (!this.txGasPrices[this.defaultFeeDenom]) {
                const newDefaultFeeDenom = (_a = minGasPrices[0]) === null || _a === void 0 ? void 0 : _a.denom;
                if (newDefaultFeeDenom) {
                    console.warn(`default fee denom ${this.defaultFeeDenom} not supported, using ${newDefaultFeeDenom} instead.`);
                    this.defaultFeeDenom = newDefaultFeeDenom;
                }
            }
        });
    }
    reloadAccountSequence() {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.sequenceInvalidated)
                this.sequenceInvalidated = false;
            try {
                const info = yield this.getQueryClient().chain.getSequence(this.bech32Address);
                const pubkey = (_b = (_a = this.accountInfo) === null || _a === void 0 ? void 0 : _a.pubkey) !== null && _b !== void 0 ? _b : {
                    type: "tendermint/PubKeySecp256k1",
                    value: this.publicKey.toString("base64"),
                };
                const chainId = (_e = (_d = (_c = this.accountInfo) === null || _c === void 0 ? void 0 : _c.chainId) !== null && _d !== void 0 ? _d : this.chainId) !== null && _e !== void 0 ? _e : yield this.getQueryClient().chain.getChainId();
                this.accountInfo = Object.assign(Object.assign({}, info), { address: this.bech32Address, pubkey,
                    chainId });
            }
            catch (error) {
                if (!this.isNewAccountError(error))
                    throw error;
            }
        });
    }
    estimateTxFee(messages, feeDenom = this.defaultFeeDenom) {
        const denomGasPrice = this.getGasPrice(feeDenom);
        ;
        let totalGasCost = messages.reduce((result, message) => {
            const gasCost = this.getGasCost(message.typeUrl);
            return result.plus(gasCost);
        }, number_1.BN_ZERO);
        let totalFees = totalGasCost.times(denomGasPrice);
        // override zero gas cost tx with some gas for tx execution
        // set overall fee to zero, implying 0 gas price.
        if (totalGasCost.isZero()) {
            totalGasCost = constant_1.DEFAULT_GAS;
            totalFees = number_1.BN_ZERO;
        }
        return {
            amount: [{
                    amount: totalFees.toString(10),
                    denom: feeDenom,
                }],
            gas: totalGasCost.toString(10),
        };
    }
    getQueryClient() {
        if (!this.query)
            throw new Error("wallet not initialized");
        return this.query;
    }
}
exports.CarbonWallet = CarbonWallet;
(function (CarbonWallet) {
    CarbonWallet.TxRaw = Object.assign({}, tx_2.TxRaw);
})(CarbonWallet = exports.CarbonWallet || (exports.CarbonWallet = {}));
